import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Play, Lock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { LOGO_URL } from "@/lib/branding";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EpisodeCard from "@/components/EpisodeCard";

export default function Landing() {
  const [episodes, setEpisodes] = useState([]);
  useEffect(() => {
    api.get("/episodes").then(r => setEpisodes(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-brand-bg text-white min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-[92vh] overflow-hidden grain" data-testid="landing-hero">
        {/* Pure dark + neon background — no stock imagery */}
        <div className="absolute inset-0" style={{
          background:
            "radial-gradient(60% 80% at 80% 20%, rgba(255,0,85,0.25) 0%, transparent 60%)," +
            "radial-gradient(40% 60% at 10% 80%, rgba(0,240,255,0.10) 0%, transparent 70%)," +
            "linear-gradient(180deg, #08020a 0%, #050505 60%, #050505 100%)",
        }} />
        <div className="absolute inset-0 hero-grad" />
        {/* Subtle stripes */}
        <div className="absolute inset-y-0 right-0 w-[55%] opacity-[0.06] pointer-events-none"
             style={{ backgroundImage: "repeating-linear-gradient(115deg, #fff 0 1px, transparent 1px 18px)" }} />

        <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-16">
          <div className="flex items-center gap-3 mb-6 animate-fade-up">
            <img src={LOGO_URL} alt="ball0ut.ent" className="h-12 w-12 object-contain drop-shadow-[0_0_12px_rgba(255,180,0,0.45)]" />
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-brand-primary/60 bg-brand-primary/10 text-brand-primary text-[11px] uppercase tracking-[0.3em] font-semibold">
              <Sparkles className="h-3 w-3" /> ball0ut.ent · Exclusive Premiere
            </div>
          </div>
          <h1 className="font-display text-6xl md:text-8xl lg:text-9xl uppercase leading-[0.92] tracking-tight max-w-5xl animate-fade-up">
            Boyz <span className="text-brand-primary neon-text">Gone</span> Wild
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-white/80 leading-relaxed animate-fade-up" style={{ animationDelay: "120ms" }}>
            The wildest, most unfiltered boyz in one location. <span className="text-white font-semibold">No rules. No limits. All on footage.</span>
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4 animate-fade-up" style={{ animationDelay: "240ms" }}>
            <Link to="/subscribe" data-testid="hero-subscribe-btn">
              <Button className="bg-brand-primary hover:bg-brand-primaryHover text-white rounded-none px-8 h-14 text-base font-semibold uppercase tracking-[0.15em] animate-glow-pulse">
                Start Watching · $6.99/mo
              </Button>
            </Link>
            <Link to="/show/boyz-gone-wild" data-testid="hero-watch-trailer">
              <Button variant="outline" className="border-white/30 bg-white/5 hover:bg-white/15 text-white rounded-none px-8 h-14 text-base uppercase tracking-[0.15em] backdrop-blur">
                <Play className="h-4 w-4 mr-2" /> See the show
              </Button>
            </Link>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-primary/60 to-transparent" />
      </section>

      {/* EPISODES PREVIEW */}
      <section className="py-20 md:py-28 max-w-[1500px] mx-auto px-6 md:px-12" data-testid="landing-episodes">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-brand-primary mb-3">Season 1 · Episodes</div>
            <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tight">Episodes coming in hot</h2>
          </div>
          <Link to="/subscribe" className="hidden md:flex items-center gap-1 text-sm uppercase tracking-[0.18em] text-brand-muted hover:text-white">
            Unlock all <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {episodes.slice(0, 4).map((ep) => (
            <div key={ep.id} className="pointer-events-none">
              <EpisodeCard ep={ep} locked />
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section className="relative py-24 overflow-hidden" data-testid="landing-pricing">
        <div className="absolute inset-0" style={{
          background:
            "radial-gradient(50% 60% at 50% 0%, rgba(255,0,85,0.18) 0%, transparent 60%)," +
            "linear-gradient(180deg, #050505 0%, #0a0306 60%, #050505 100%)",
        }} />
        <div className="relative z-10 max-w-[1100px] mx-auto px-6 md:px-12 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-brand-primary mb-4">Membership</div>
          <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tight max-w-4xl mx-auto">
            One plan. <span className="text-brand-primary neon-text">Everything unlocked.</span>
          </h2>

          <div className="mt-12 inline-block border border-white/10 bg-brand-surface p-10 text-left max-w-md w-full">
            <div className="text-xs uppercase tracking-[0.3em] text-brand-primary mb-3">BallOut Premium</div>
            <div className="flex items-end gap-2">
              <div className="font-display text-7xl text-white leading-none">$6.99</div>
              <div className="text-brand-muted pb-2">/ month</div>
            </div>
            <ul className="mt-8 space-y-3 text-sm text-white/90" data-testid="landing-pricing-features">
              {[
                "Full access to every episode",
                "New drops every week",
                "Cancel anytime",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 bg-brand-primary shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Link to="/subscribe" data-testid="pricing-cta">
              <Button className="mt-9 w-full bg-brand-primary hover:bg-brand-primaryHover text-white rounded-none h-13 py-4 uppercase tracking-[0.15em] font-semibold">
                Start now
              </Button>
            </Link>
            <div className="text-[11px] text-brand-muted mt-4 text-center uppercase tracking-[0.18em]">
              Secure payments by Stripe
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
